import {EnrollmentApplication} from 'Enrollment/models/EnrollmentApplication';
import {AccountDetail} from 'Agent/Accounts/models/AccountDetail';
import {AlertPayload} from 'Core/ducks/alerts';
import {EnrollmentFileNotes} from 'Enrollment/models/EnrollmentFileNotes';
import {IO} from 'fp-ts/lib/IO';
import {Option} from 'fp-ts/lib/Option';
import {FilterFieldDefinitions} from 'Shared/components/Table/Table';
import {createAction} from 'Shared/utilities/actionCreator';
import unionize, {ofType, UnionOf} from 'unionize';
import {PreregisteredUserFilter} from 'Users/ducks/userAdministration';
import {UserClaims} from 'Users/models/Claims';
import {UserModel} from 'Users/models/UserModel';
import {ValidApplication, InvalidApplication} from 'ApplicationEntry/models/Application';
import {AgentAgencyClaim} from 'Users/models/AgentAgencyClaim';

enum ModalActionType {
  HIDE_MODAL = 'modal/HIDE_MODAL',
  SHOW_MODAL = 'modal/SHOW_MODAL',
}

export const ModalState = unionize({
  AddAgent: ofType<{}>(),
  Alerts: ofType<AlertData>(),
  UsersRegistration: ofType<UserRegistrationData>(),
  LegacySignOn: ofType<{}>(),
  RequestPolicyInfoReport: ofType<{userClaims: UserClaims; accountDetail: AccountDetail; email: string}>(),
  None: ofType<{}>(),
  OffboardingConfirmation: ofType<AgentOffboardingData>(),
  SwitchTaxId: ofType<{agencyAssociations: AgentAgencyClaim[]}>(),
  ShareLink: ofType<{}>(),
  UploadEnrollmentApplication: ofType<UploadEnrollmentApplicationData>(),
  EnrollmentFileRejectsEmail: ofType<{emailId?: string; groupId?: string; isResend: boolean}>(),
  MobileFilter: ofType<MobileFilter<any>>(),
  ClaimsConfirmation: ofType<{policyNumber: string}>(),
  LegacyAccess: ofType<{}>(),
  DeleteBeneficiaryConfirmation: ofType<{policyNumber: string; id: string}>(),
  EnrollmentFilesNotProcessedNotes: ofType<{notes: EnrollmentFileNotes[]}>(),
  EnrollmentApplicationUpload: ofType<{fileId?: string; groupId?: string}>(),
  EnrollmentFileErrorDeleteConfirmation: ofType<{caseRequestId: string}>(),
  ExternalUploadQueueRemoveConfirmation: ofType<{fileId: string, uploadedByEmail: string}>(),
  ExternalUploadReturnToBroker: ofType<{returnComments: string, removalReason:string,  recentActionDate?: Date | null;}>(),
  UserDetails: ofType<{firstName: string, lastName:string,  email: string;}>(),
  ExternalUploadQueueProcessConfirmation: ofType<{fileId: string}>(),
  BrokerSpreadSheetCancelConfirmation: ofType<{fileId: string}>(),
  DeletePendingRegistrationConfirmation: ofType<{
    id: string;
    filter: PreregisteredUserFilter;
    encryptedIdentifier?: string;
  }>(),
  SSOConfirmation: ofType<{}>(),
  ReviewSecurityQuestionsAndResetPassword: ofType<UserClaims>(),
  ClientOnboardingUpload: ofType<{}>(),
  GenelcoEnrollmentApplications: ofType<{enrollmentFile: EnrollmentApplication}>(),
  SimplifiedEnrollmentFileUploadCancelConfirmation: ofType<{fileId: string}>(),
  GenelcoUploadInternalSystemError: ofType<{}>(),
  VerifyCriticalInformation: ofType<VerifyCriticalInformationData>(),
  ExternalUploadQueueAdditionalDetails: ofType<{reason?: string, reasonType:string, returnComments:string}>(),
});

export type ModalStateUnion = UnionOf<typeof ModalState>;

type UploadEnrollmentApplicationData = {
  accountId: string;
  accountNumber: string;
  servicingAgentNumber?: string;
};
type UserRegistrationData = {
  onClose: () => void;
};

type AlertData = {
  messages: AlertPayload[];
  onDismissAlerts: IO<void>;
};

type AgentOffboardingData = {
  agent: UserModel;
};

type MobileFilter<TFilter> = {
  filter: TFilter;
  filters?: FilterFieldDefinitions<TFilter>;
  totalResults: Option<number>;
};

type VerifyCriticalInformationData = {
  application: ValidApplication | InvalidApplication;
};

export const showModal = (modalState: ModalStateUnion) => createAction(ModalActionType.SHOW_MODAL, {modalState});
export const hideModal = () => createAction(ModalActionType.HIDE_MODAL);

export const DEFAULT_STATE: ModalState = {
  modalState: ModalState.None(),
};

type ModalState = {
  modalState: ModalStateUnion;
};

type ModalAction = ReturnType<typeof showModal> | ReturnType<typeof hideModal>;

export default (state: ModalState = DEFAULT_STATE, action: ModalAction): ModalState => {
  switch (action.type) {
    case ModalActionType.SHOW_MODAL:
      return {
        ...state,
        modalState: action.payload.modalState,
      };
    case ModalActionType.HIDE_MODAL:
      return {
        ...state,
        modalState: ModalState.None(),
      };
    default:
      return state;
  }
};
